// =============================================================================
// Trip status presentation helpers
//
// Shared between every place that renders a triplink trip-status pill —
// the Booked Trips cards, the Admin Bookings list, the patient-lookup
// duplicate-detection panel, and (eventually) any reporting view. The
// label + colour mapping is identical everywhere so dispatchers learn
// it once.
//
// Keep this list in sync with triplink's enumeration. Unknown values
// fall through to a neutral border so a new status they introduce
// later doesn't render as invisible / broken.
// =============================================================================

/** Tailwind border + text classes per triplink status. Includes every
 *  "_canceled" variant explicitly so the muted zinc treatment lands
 *  consistently regardless of which actor pulled the trigger.
 *
 *  Each entry pairs a *light-mode* default (deeper shade, AA contrast
 *  on white card) with a `dark:` variant (the original lighter shade
 *  that pops on the navy card). Tailwind's `dark:` is wired to the
 *  `data-theme="dark"` attribute in `tailwind.config.ts`, so the
 *  switch is the user's theme pick, not their OS setting.
 *
 *  Border opacity stays at the same /45 on both themes — the slight
 *  loss of contrast on white at /45 reads as "subtle", which is the
 *  intent of a pill border (the text carries the meaning). */
export const STATUS_BADGE_CLASSES: Record<string, string> = {
  available:
    "border-orange-500/55 text-orange-700 dark:border-orange-400/45 dark:text-orange-400",
  pending:
    "border-amber-500/55 text-amber-700 dark:border-amber-400/45 dark:text-amber-400",
  will_call:
    "border-sky-500/55 text-sky-700 dark:border-sky-400/45 dark:text-sky-400",
  draft:
    "border-indigo-500/55 text-indigo-700 dark:border-indigo-400/45 dark:text-indigo-400",
  assigned:
    "border-emerald-500/55 text-emerald-700 dark:border-emerald-400/45 dark:text-emerald-400",
  en_route:
    "border-green-600/55 text-green-700 dark:border-green-500/45 dark:text-green-400",
  arrived:
    "border-fuchsia-500/55 text-fuchsia-700 dark:border-fuchsia-400/45 dark:text-fuchsia-400",
  in_progress:
    "border-violet-600/55 text-violet-700 dark:border-violet-500/45 dark:text-violet-400",
  at_destination:
    "border-indigo-500/55 text-indigo-700 dark:border-indigo-400/45 dark:text-indigo-400",
  re_route:
    "border-cyan-600/55 text-cyan-700 dark:border-cyan-400/45 dark:text-cyan-400",
  finished:
    "border-slate-500/55 text-slate-600 dark:border-slate-400/45 dark:text-slate-400",
  customer_canceled:
    "border-zinc-500/55 text-zinc-600 dark:border-zinc-400/45 dark:text-zinc-400",
  customer_requested_cancel:
    "border-zinc-500/55 text-zinc-600 dark:border-zinc-400/45 dark:text-zinc-400",
  organization_canceled:
    "border-zinc-500/55 text-zinc-600 dark:border-zinc-400/45 dark:text-zinc-400",
  fleet_canceled:
    "border-zinc-500/55 text-zinc-600 dark:border-zinc-400/45 dark:text-zinc-400",
  member_canceled:
    "border-zinc-500/55 text-zinc-600 dark:border-zinc-400/45 dark:text-zinc-400",
  br_canceled:
    "border-zinc-500/55 text-zinc-600 dark:border-zinc-400/45 dark:text-zinc-400",
  co_canceled:
    "border-zinc-500/55 text-zinc-600 dark:border-zinc-400/45 dark:text-zinc-400",
  deleted:
    "border-zinc-500/55 text-zinc-600 dark:border-zinc-400/45 dark:text-zinc-400",
  late_cancel:
    "border-rose-500/55 text-rose-700 dark:border-rose-400/45 dark:text-rose-400",
  no_show:
    "border-rose-500/55 text-rose-700 dark:border-rose-400/45 dark:text-rose-400",
};

/** Human-readable label per triplink status. Lives beside the color
 *  map so the two enumerations stay in sync — a status that gets a
 *  color should also get a label that doesn't read as backend jargon
 *  ("br_canceled" used to render as "BR CANCELED" on the pills). */
export const STATUS_LABELS: Record<string, string> = {
  available: "Available",
  pending: "Pending",
  will_call: "Will Call",
  draft: "Draft",
  assigned: "Assigned",
  en_route: "En Route",
  arrived: "Arrived",
  in_progress: "In Progress",
  at_destination: "At Destination",
  re_route: "Re-Routed",
  finished: "Finished",
  customer_canceled: "Canceled — Customer",
  customer_requested_cancel: "Cancel Requested",
  organization_canceled: "Canceled — Organization",
  fleet_canceled: "Canceled — Fleet",
  member_canceled: "Canceled — Member",
  br_canceled: "Canceled — Broker",
  co_canceled: "Canceled — Company",
  deleted: "Deleted",
  late_cancel: "Late Cancel",
  no_show: "No Show",
};

/** Human-readable label. Known statuses map through STATUS_LABELS;
 *  unknown upstream values fall back to the underscore→space swap so
 *  a new triplink state still renders something legible. */
export function prettyStatus(status: string): string {
  const k = String(status ?? "").toLowerCase();
  return STATUS_LABELS[k] ?? String(status ?? "").replace(/_/gu, " ");
}

/** Tailwind class string ready to slot into a `cn()` call. Falls back
 *  to a neutral border for unknown statuses (e.g. a new triplink
 *  state) so we don't render invisible chips. */
export function statusBadgeClasses(status: string): string {
  const k = String(status ?? "").toLowerCase();
  return STATUS_BADGE_CLASSES[k] ?? "border-border text-foreground/80";
}

/** Returns true for any "*_canceled" / "late_cancel" / "no_show" /
 *  "deleted" status — the rows we hide behind the "Hide cancelled"
 *  toggle on Admin → Bookings. */
export function isTerminalCancelStatus(status: string): boolean {
  const k = String(status ?? "").toLowerCase();
  return (
    k.endsWith("canceled") ||
    k.endsWith("cancelled") ||
    k === "late_cancel" ||
    k === "no_show" ||
    k === "deleted"
  );
}

/** Coarse status group used by the chip-row filter at the top of
 *  Admin → Bookings. Maps the 20+ raw triplink statuses down to the
 *  six buckets dispatch actually thinks in. */
export type StatusGroup =
  | "all"
  | "pending"
  | "will_call"
  | "assigned"
  | "in_progress"
  | "finished"
  | "canceled";

export function groupForStatus(status: string): StatusGroup {
  const k = String(status ?? "").toLowerCase();
  if (!k) return "pending";
  if (k === "pending" || k === "available" || k === "draft") return "pending";
  if (k === "will_call") return "will_call";
  if (k === "assigned") return "assigned";
  if (
    k === "en_route" ||
    k === "arrived" ||
    k === "in_progress" ||
    k === "at_destination" ||
    k === "re_route"
  )
    return "in_progress";
  if (k === "finished") return "finished";
  if (isTerminalCancelStatus(k)) return "canceled";
  return "pending";
}
